import axios from 'axios'

import type { CustomerImport, CustomerImportFileReadStatus } from '@/types/types'

const FILE_READ_POLL_INTERVAL_MS = 2000
const FILE_READ_MAX_ATTEMPTS = 90

const VALUE_MAPPING_TYPES = ['store', 'optin', 'has_optin_email', 'gender']

export type CustomerImportFileReadErrorCode = 'failed' | 'timeout'

export class CustomerImportFileReadError extends Error {
  constructor(
    public readonly code: CustomerImportFileReadErrorCode,
    public readonly detail?: string | null
  ) {
    super(detail ?? code)
    this.name = 'CustomerImportFileReadError'
  }
}

export const isCustomerImportFileReadError = (error: unknown): error is CustomerImportFileReadError =>
  error instanceof CustomerImportFileReadError

export const hasPendingValueMapping = (columns: CustomerImport['columns'] | undefined): boolean => {
  return columns?.some((column) => VALUE_MAPPING_TYPES.includes(column.type) && !column.values?.length) ?? false
}

export const pollCustomerImportUntilFileReady = async (
  importId: number,
  options?: { waitForValueMapping?: boolean }
) => {
  for (let attempt = 0; attempt < FILE_READ_MAX_ATTEMPTS; attempt++) {
    const response = await axios.get(`/customer-imports/${importId}`)
    const customerImport = response.data.data.customerImport
    const status = customerImport.fileReadStatus as CustomerImportFileReadStatus

    if (status === 'failed') {
      throw new CustomerImportFileReadError('failed', customerImport.fileReadError)
    }

    if (status === 'ready' && (!options?.waitForValueMapping || !hasPendingValueMapping(customerImport.columns))) {
      return response
    }

    await new Promise((resolve) => setTimeout(resolve, FILE_READ_POLL_INTERVAL_MS))
  }

  throw new CustomerImportFileReadError('timeout')
}
